package ui.app.visibility


import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.foundation.shape.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import api.*
import common.frontend.api.*
import compose.assets.*
import compose.assets.svg.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.app.assign.*
import ui.widget.*

data class AppVisibilityListingArgs(var companyId: Long = -1)


@ComposableNavNode(AppVisibilityListingArgs::class)
@Composable
fun AppVisibilityListingUI() {
    with(AppVisibilityScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun AppVisibilityScope.Content(nnvl: AppVisibilityScope.Wrapper) {
    val visibleAppIds = remember {
        mutableStateOf(nnvl.data.variantArr.map { it.id })
    }
    LaunchedEffect(vm.searchFiledAppVariant.value) {
        val typeNames = AppType.all.associateBy(
            keySelector = { it.type },
            valueTransform = { it.typeName().await() })

        visibleAppIds.value = SearchWrapper.use(
            fields = { entry ->
                listOf(entry.data.name, (typeNames[entry.data.type] ?: ""))
            },
            items = nnvl.listingVariants.state,
            query = vm.searchFiledAppVariant,
            sort = compareBy<AppVisibilityScope.WrapperVariant.VariantItem> { AppType.RSS match it.data.type }
                .thenBy { it.data.id }
        ).map { it.data.id }
    }
    Column(
        Modifier.fillMaxSize()
            .padding(top = 8.dp)
    ) {
        Toolbar(nnvl)

        val rowScope = RowScope.remember()
        Box(Modifier.fillMaxSize()) {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(start = 8.dp, bottom = 32.dp),
            ) {
                stickyHeader {
                    Header(nnvl, visibleAppIds, rowScope)
                }
                itemsIndexed(
                    SearchWrapper.use(
                        fields = arrayOf(Io.ListingAppManager.ItemCompany::name),
                        items = nnvl.listingCompany.state,
                        query = vm.searchFiledCompany,
                        sort = compareByDescending { it.data.id }
                    )) { _, item ->
                    Item(item, visibleAppIds, rowScope)
                }
                item {
                    Footer(visibleAppIds, rowScope)
                }
            }
            HorizontalScrollbar(
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .padding(8.dp)
                    .fillMaxWidth(),
                adapter = rememberScrollbarAdapter(rowScope.stateHorizontal)
            )
        }
    }
}


@Composable
private fun AppVisibilityScope.Toolbar(nnvl: AppVisibilityScope.Wrapper) {
    if (display.isMobile && !display.isLand()) {
        Column(
            modifier = Modifier.padding(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                HomeSearchUI(
                    filed = vm.searchFiledCompany,
                    label = Res.string.company_detail__title,
                    modifier = Modifier.weight(1F)
                )

                AnimatedVisibility(nnvl.change) {
                    IconButton({ action(AppVisibilityListingVM.Action.Save) }) {
                        Icon(Svg.Content_save, null)
                    }
                }
            }
            SearchUI(
                filed = vm.searchFiledAppVariant,
                modifier = Modifier.fillMaxWidth(),
                label = Res.string.home_panel__layer_bottom,
            )
        }
    } else {
        Row(
            modifier = Modifier.padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            HomeSearchUI(
                filed = vm.searchFiledCompany,
                label = Res.string.company_detail__title,
                modifier = Modifier.weight(1F)
            )
            SearchUI(
                filed = vm.searchFiledAppVariant,
                modifier = Modifier.weight(1F),
                label = Res.string.home_panel__layer_bottom,
            )
            AnimatedVisibility(nnvl.change) {
                IconButton({ action(AppVisibilityListingVM.Action.Save) }) {
                    Icon(Svg.Content_save, null)
                }
            }
        }
    }
}


@Composable
private fun AppVisibilityScope.HeaderItem(
    modifier: Modifier,
    item: AppVisibilityScope.WrapperVariant.VariantItem
) {
    Column(modifier.width(192.dp).padding(4.dp)) {
        Text(
            text = AppType[item.data.type].typeName().rememberString(),
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.fillMaxWidth(),
            style = MaterialTheme.typography.labelMedium,
            textAlign = TextAlign.Center,
            maxLines = 1
        )
        if (AppType.RSS match item.data.type) {
            Text(
                text = item.data.name,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.fillMaxWidth(),
                style = MaterialTheme.typography.labelSmall,
                textAlign = TextAlign.Center,
                maxLines = 2
            )
        }
    }
}

@Stable
private data class RowScope(
    val titleOffset: State<Dp>,
    val checkOffset: State<Dp>,
    val stateHorizontal: ScrollState
) {
    companion object {
        @Composable
        fun remember(): RowScope {
            val stateHorizontal = rememberScrollState(0)
            val titleOffset = animateDpAsState(
                if (stateHorizontal.value < 184F) (maxOf(8F, (25F * (1F - ((stateHorizontal.value / 184F)))))).dp
                else 8.dp

            )
            val checkOffset = animateDpAsState(
                if (stateHorizontal.value < 1) 10.dp
                else if (stateHorizontal.value < 128F) minOf(20F, maxOf(10F, 20F * (((stateHorizontal.value / 128F))))).dp
                else 20.dp
            )
            return remember {
                RowScope(
                    titleOffset,
                    checkOffset,
                    stateHorizontal
                )
            }
        }
    }
}

@Composable
private fun Footer(visibleAppIds: MutableState<List<Long>>, rowScope: RowScope) {
    Box(
        Modifier.fillMaxWidth()
            .padding(end = 8.dp)
            .horizontalScroll(rowScope.stateHorizontal)
    ) {
        Spacer(
            Modifier
                .height(8.dp)
                .width(visibleAppIds.value.size.let {
                    ((it + 1) * 192).dp
                })
                .background(
                    color = MaterialTheme.colorScheme.surfaceContainer,
                    shape = RoundedCornerShape(
                        bottomStart = 8.dp,
                        bottomEnd = 8.dp
                    )
                )
        )
    }
}

@Composable
private fun AppVisibilityScope.Header(
    nnvl: AppVisibilityScope.Wrapper,
    visibleAppIds: MutableState<List<Long>>,
    rowScope: RowScope
) {
    Box(
        Modifier.fillMaxWidth()
            .padding(end = 8.dp)
            .horizontalScroll(rowScope.stateHorizontal)
    ) {
        Row(
            Modifier.background(
                color = MaterialTheme.colorScheme.surfaceContainer,
                shape = RoundedCornerShape(
                    topStart = 8.dp,
                    topEnd = 8.dp
                )
            )
        ) {
            Space(192.dp, 1.dp)
            nnvl.listingVariants.state
                .filter { it.data.id in visibleAppIds.value }
                .forEach { entry ->
                    HeaderItem(
                        modifier = Modifier.align(Alignment.CenterVertically),
                        item = entry
                    )
                }
        }
    }


//    Row(
//        modifier = Modifier.fillMaxWidth()
//    ) {
//        Spacer(Modifier.width(192.dp))
//        Box(
//            Modifier.fillMaxWidth()
//                .horizontalScroll(stateHorizontal)
//        ) {
//            Row(Modifier.background(MaterialTheme.colorScheme.surfaceContainer, RoundedCornerShape(topEnd = 8.dp))) {
//                nnvl.listingVariants.state
//                    .filter { it.data.id in visibleAppIds.value }
//                    .forEach {
//                        VerticalDivider(Modifier.height(16.dp).align(Alignment.CenterVertically))
//                        HeaderItem(
//                            modifier = Modifier.align(Alignment.CenterVertically),
//                            item = it
//                        )
//                    }
//            }
//        }
//    }
}

@Composable
private fun AppVisibilityScope.Item(
    item: AppVisibilityScope.WrapperCompany.CompanyItem,
    visibleAppIds: MutableState<List<Long>>,
    rowScope: RowScope
) {
    Box(
        Modifier
            .fillMaxWidth()
            .height(64.dp)
    ) {


        Column(
            Modifier
                .fillMaxHeight()
                .padding(end = 8.dp)
                .horizontalScroll(rowScope.stateHorizontal)
        ) {
            HorizontalDivider(
                modifier = Modifier
                    .background(MaterialTheme.colorScheme.surfaceContainer)
                    .width(item.variants.state
                        .count { it.data.id in visibleAppIds.value }
                        .let { ((it + 1) * 192).dp })
                    .padding(horizontal = 8.dp)
            )
            Row(
                Modifier.fillMaxHeight()
                    .background(MaterialTheme.colorScheme.surfaceContainer)
            ) {
                Space(192.dp, 1.dp)
                item.variants.state
                    .filter { it.data.id in visibleAppIds.value }
                    .forEach { entry ->
                        Box(Modifier.width(192.dp)
                            .fillMaxHeight()
                            .clickable { entry.enabled.set(!entry.enabled.state) }) {
                            Checkbox(
                                checked = entry.enabled.state,
                                modifier = Modifier
                                    .align(Alignment.TopCenter)
                                    .offset(y = rowScope.checkOffset.value),
                                onCheckedChange = {
                                    entry.enabled.set(it)
                                })
                        }
                    }
            }
        }
        Text(
            text = item.data.name,
            modifier = Modifier.fillMaxWidth()
                .padding(start = 8.dp)
                .width(192.dp)
                .offset(y = rowScope.titleOffset.value),
            overflow = TextOverflow.Ellipsis,
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1
        )
    }

}
